<template>
    <input v-bind="$attrs" :type="type" :value="displayValue" :disabled="disabled" :min="min" :max="max" @input="updateValue($event.target.value)" @focus="removeFormatting" @blur="addFormatting" :class="classList" ref="input" :placeholder="placeholder" />
    <span v-if="subTitle" class="input-sub-text">
        <slot name="sub-text">{{ subTitle }}</slot>
    </span>
    <span v-if="error" class="error-text">{{ getErrorMessage }}</span>
</template>

<script>
export default {
    inheritAttrs: false, // Prevent automatic inheritance of non-props attributes
    props: {
        modelValue: {
            type: [String, Number],
            default: '' // Default to an empty string
        },
        type: {
            type: String,
            default: 'text'
        },
        subTitle: {
            type: String
        },
        disabled: {
            type: Boolean,
            default: false
        },
        error: {
            type: [String, Array, Object],
            default: null
        },
        placeholder: {
            type: String
        },
        min: {
            type: [String, Number],
            default: null
        },
        max: {
            type: [String, Number],
            default: null
        },
        formatNumber: {
            type: Boolean,
            default: false
        },
        suffix: {
            type: String,
            default: ''
        }
    },
    emits: ['update:modelValue', 'input', 'focus', 'blur'],
    data() {
        return {
            input: null,
            internalValue: this.modelValue,
            isFocused: false
        };
    },
    computed: {
        getErrorMessage() {
            if (!this.error) return '';

            if (typeof this.error === 'string') {
                return this.error;
            }

            if (Array.isArray(this.error)) {
                return this.error[0];
            }

            // If error is an object with keys
            if (typeof this.error === 'object') {
                const firstKey = Object.keys(this.error)[0];
                if (firstKey && this.error[firstKey]) {
                    return Array.isArray(this.error[firstKey]) ? this.error[firstKey][0] : this.error[firstKey];
                }
            }

            return '';
        },
        displayValue() {
            if (this.formatNumber && !this.isFocused) {
                // Format the number with spaces for thousands, preserving decimals only if they exist
                const parts = (this.internalValue?.toString() || '').split('.') || [];
                parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ' ');
                const formattedValue = parts.length > 1 ? parts.join('.') : parts[0];
                return this.internalValue ? (this.suffix ? `${formattedValue} ${this.suffix}` : formattedValue) : '';
            }
            return this.internalValue || ''; // Ensure fallback to empty string
        },
        classList() {
            const baseClasses = 'w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500';
            const errorClasses = this.error ? 'border-red-500 focus:border-red-500 focus:ring-red-500' : '';
            const disabledClasses = this.disabled ? 'disabled-input' : '';
            return `${baseClasses} ${errorClasses} ${disabledClasses}`.trim();
        },
    },
    mounted() {
        this.input = this.$refs.input;
        if (this.input && this.input.hasAttribute('autofocus')) {
            this.input.focus();
        }
    },
    methods: {
        focus() {
            this.input.focus();
        },
        removeFormatting() {
            this.isFocused = true;
            if (this.formatNumber && this.internalValue != null) {
                // Preserve the decimal point while removing other non-numeric characters
                this.internalValue = this.internalValue.toString().replace(/[^0-9.]/g, '');
            }
        },
        addFormatting() {
            this.isFocused = false;
            // Do not modify internalValue here; formatting is handled by displayValue
        },
        updateValue(value) {
            if (this.formatNumber) {
                // Allow only numbers and a single decimal point
                const numericValue = value?.replace(/[^0-9.]/g, '').replace(/(\..*?)\..*/g, '$1') || '';
                this.internalValue = numericValue;
                this.$emit('update:modelValue', numericValue); // Emit raw numeric value
            } else {
                this.internalValue = value || ''; // Ensure fallback to empty string
                this.$emit('update:modelValue', this.internalValue);
            }
        }
    },
    watch: {
        modelValue(newVal) {
            this.internalValue = newVal;
        }
    }
};
</script>
<style scoped>
.input-sub-text {
    display: block;
    margin-top: -10px;
    font-size: 0.75rem;
    margin-left: 5px;
    color: #966A1D;
}

.disabled-input {
    opacity: 0.5;
    pointer-events: none;
}

.error {
    border: 1px solid #FF6F61;
}

.error-text {
    color: #FF6F61;
    font-size: 1rem;
    display: block;
}
</style>